import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Receipt, TrendUp, CurrencyGbp, Cube, CheckCircle, LockKey, Warning, ArrowRight } from "@phosphor-icons/react";
import { useModules, MODULE_ORDER } from "@/context/ModulesContext";

const META = {
  prepayments: { icon: Receipt, blurb: "Spread costs paid in advance across the periods they cover." },
  accruals: { icon: TrendUp, blurb: "Recognise costs you've incurred before the bill has arrived." },
  deferred_income: { icon: CurrencyGbp, blurb: "Release income received in advance as it's earned." },
  fixed_assets: { icon: Cube, blurb: "Track assets and post depreciation on a schedule." },
};

export function ModulesPanel({ onChange, historyKeys }) {
  const { modules, labels, tenantId, loading, refresh } = useModules();
  const [confirmKey, setConfirmKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const isPreExisting = (key) => Array.isArray(historyKeys) && !historyKeys.includes(key);

  if (loading) {
    return <div data-testid="modules-loading" className="font-mono text-navy/65 text-sm tracking-widest py-8">LOADING…</div>;
  }
  if (!tenantId || !modules) {
    return (
      <div data-testid="modules-no-entity" className="inline-flex items-center gap-2 text-xs bg-warn/10 border border-warn/40 text-warn rounded-lg px-3 py-1.5">
        <Warning size={14} weight="fill" /> Connect &amp; activate an organisation to manage its modules.
      </div>
    );
  }

  const enable = async (key) => {
    setBusy(true);
    try {
      await api.post("/entity/modules/enable", { enable: [key] });
      toast.success(`${labels[key]} switched on`);
      setConfirmKey(null);
      await refresh();
      window.dispatchEvent(new Event("modules:refresh"));
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch the module on");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="modules-panel">
      <div className="grid sm:grid-cols-2 gap-4">
        {MODULE_ORDER.map((key) => {
          const on = !!modules[key];
          const Icon = META[key].icon;
          return (
            <div key={key} data-testid={`module-card-${key}`}
              className={`rounded-xl border p-5 transition-colors ${on ? "border-positive/40 bg-positive/[0.04]" : "border-slate-200 bg-white"}`}>
              <div className="flex items-start gap-3">
                <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${on ? "bg-positive/15" : "bg-navy/[0.06]"}`}>
                  <Icon size={22} weight="fill" className={on ? "text-positive" : "text-navy"} style={on ? { color: "#00A859" } : undefined} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-semibold text-navy">{labels[key] || key}</h4>
                    {on && (
                      <span data-testid={`module-status-${key}`} className="inline-flex items-center gap-1 text-[0.62rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-positive/15" style={{ color: "#00885b" }}>
                        <LockKey size={10} weight="fill" /> {isPreExisting(key) ? "Active (pre-existing)" : "Active"}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-navy/70 mt-1">{META[key].blurb}</p>
                </div>
              </div>
              <div className="mt-4">
                {on ? (
                  <div data-testid={`module-locked-${key}`} className="flex items-center gap-1.5 text-xs font-medium" style={{ color: "#00885b" }}>
                    <CheckCircle size={15} weight="fill" /> {isPreExisting(key) ? "Active before module tracking — cannot be turned off" : "Switched on — cannot be turned off"}
                  </div>
                ) : (
                  <button onClick={() => setConfirmKey(key)} disabled={busy} data-testid={`module-enable-${key}`}
                    className="inline-flex items-center gap-1.5 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-navy transition-colors disabled:opacity-60" style={{ background: "#F37A24" }}>
                    Switch on <ArrowRight size={14} weight="bold" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {confirmKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="module-confirm-overlay"
          onClick={() => !busy && setConfirmKey(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" data-testid="module-confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-1">
              <span className="w-10 h-10 rounded-xl bg-orange/15 flex items-center justify-center shrink-0">
                <Warning size={20} weight="fill" style={{ color: "#F37A24" }} />
              </span>
              <div>
                <h3 className="font-heading font-bold text-navy text-lg">Switch on {labels[confirmKey]}?</h3>
                <p className="text-sm text-navy/70 mt-1.5">
                  This turns on the <span className="font-semibold text-navy">{labels[confirmKey]}</span> ledger for this organisation.
                  It <span className="font-semibold text-navy">cannot be switched off later</span> — this module posts real journals to Xero.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfirmKey(null)} disabled={busy} data-testid="module-confirm-cancel"
                className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5 disabled:opacity-60">Cancel</button>
              <button onClick={() => enable(confirmKey)} disabled={busy} data-testid="module-confirm-enable"
                className="px-5 py-2 text-sm font-semibold rounded-lg text-white hover:bg-navy transition-colors disabled:opacity-60" style={{ background: "#F37A24" }}>
                {busy ? "Switching on…" : "Yes, switch it on"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ModulesPanel;
